import { RefObject, useEffect, useRef } from 'react'
import { useAnimationControls, useReducedMotion } from 'motion/react'
import { getPanelVariants, PanelShape } from './menuAnimation'

const toPx = (values: number[]) => values.map(value => `${value}px`).join(' ')

// both clip-paths are written as four insets and four radii so they can be interpolated
const getPanelShape = (panel: HTMLElement, button: HTMLElement): PanelShape => {
  const panelRect = panel.getBoundingClientRect()
  const buttonRect = button.getBoundingClientRect()
  const buttonInsets = [
    buttonRect.top - panelRect.top,
    panelRect.right - buttonRect.right,
    panelRect.bottom - buttonRect.bottom,
    buttonRect.left - panelRect.left,
  ]
  const buttonRadius = buttonRect.width / 2
  const panelRadius = parseFloat(getComputedStyle(panel).borderTopRightRadius) || 0
  return {
    collapsed: `inset(${toPx(buttonInsets)} round ${toPx(Array(4).fill(buttonRadius))})`,
    expanded: `inset(${toPx([0, 0, 0, 0])} round ${toPx([0, panelRadius, panelRadius, 0])})`,
  }
}

// Grows the panel out of the menu button when it opens and shrinks it back into the button when it closes
const usePanelMorph = (props: {
  isOpen: boolean
  buttonRef: RefObject<HTMLButtonElement | null>
  onOpenStart?: () => void
  onCloseEnd?: () => void
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const shape = useRef<PanelShape | null>(null)
  const wasOpen = useRef(false)
  const controls = useAnimationControls()
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    const panel = panelRef.current
    const button = props.buttonRef.current
    if (!panel || !button) return

    if (props.isOpen) {
      wasOpen.current = true
      // start from the button's current spot, it may have moved since the last close
      shape.current = getPanelShape(panel, button)
      controls.set('collapsed')
      props.onOpenStart?.()
      controls.start('expanded')
      return
    }

    if (!wasOpen.current) return
    wasOpen.current = false
    shape.current = getPanelShape(panel, button)
    let isReopened = false
    controls.start('collapsed').then(() => {
      if (!isReopened) props.onCloseEnd?.()
    })
    return () => {
      isReopened = true
    }
  }, [props.isOpen])

  return { panelRef, controls, variants: getPanelVariants(shape, shouldReduceMotion) }
}

export default usePanelMorph
